import { FC, useCallback, useEffect, useRef, useState } from 'react'

import { useNavigate, useSearchParams } from 'react-router-dom'

import {
  useNotifications,
  Center,
  ErrorMessage
} from '@sequencehq/core-components'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'

import { AUTO_DISPLAY_QUOTE_TEMPLATE_MODAL_STATE } from 'Cube/index'
import Spinner from 'components/Loading'

/**
 * HubSpot object ids are numeric, and 20 digits is past any real one. Belt to the endpoint's own
 * `encodeURIComponent`: this decides whether to bother the server, not whether the url is safe.
 */
const isHubspotId = (value: string): boolean => /^\d{1,20}$/.test(value)

const FAILURE_BY_STATUS: Partial<Record<number, string>> = {
  403: "You don't have access to this Sequence workspace.",
  404: "This HubSpot portal isn't connected to Sequence."
}

/** A dropped connection reaches us as a synthesised 500, so this covers both and claims neither. */
const UNREACHABLE = "The request didn't get through."

/**
 * Landing page for the "Create Sequence Quote" HubSpot card. Opens the deal's quote, creating one
 * if it has none. The link names a portal, not a Sequence account, so the wrong workspace is a
 * dead end the rep resolves themselves.
 */
export const HubspotCreateQuote: FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { displayNotification } = useNotifications()

  const [wrongWorkspace, setWrongWorkspace] = useState(false)

  const dealId = searchParams.get('dealId')
  const portalId = searchParams.get('portalId')
  // A deal need not have a company, and the card leaves the parameter off entirely when it has none.
  const companyId = searchParams.get('companyId') ?? undefined

  // Keyed on the whole request, not just the deal: the same deal reached through a different portal
  // is a different question, and dropping it would leave the page spinning with nothing in flight.
  const startedFor = useRef<string | null>(null)
  const requestKey = `${dealId}|${portalId}|${companyId}`

  const showFailure = useCallback(
    (reason: string) => {
      displayNotification(`Couldn't open a quote for this deal. ${reason}`, {
        type: 'error'
      })
      navigate('/home')
    },
    [displayNotification, navigate]
  )

  useEffect(() => {
    if (
      !dealId ||
      !portalId ||
      !isHubspotId(dealId) ||
      !isHubspotId(portalId) ||
      (companyId !== undefined && !isHubspotId(companyId))
    ) {
      showFailure('The link is missing valid deal details.')
      return
    }
    if (startedFor.current === requestKey) {
      return
    }
    startedFor.current = requestKey

    let abandoned = false

    const start = async () => {
      const response = await dashboardv99990101Client.postHubspotDealQuote({
        dealId,
        portalId,
        companyId
      })

      // The rep navigated away while this was in flight; taking them back would be a hijack.
      if (abandoned) {
        return
      }

      if (response.res.status === 409) {
        setWrongWorkspace(true)
        return
      }

      if (response.error || !response.data) {
        showFailure(FAILURE_BY_STATUS[response.res.status] ?? UNREACHABLE)
        return
      }

      displayNotification(
        response.data.outcome === 'CREATED'
          ? 'Quote created from HubSpot deal'
          : 'Opened the existing quote for this deal',
        { type: 'success' }
      )

      navigate(`/quotes/${response.data.quoteId}`, {
        // Show the apply quote template modal, see AutoDisplayQuoteTemplateModal
        state: AUTO_DISPLAY_QUOTE_TEMPLATE_MODAL_STATE
      })
    }

    // The api client resolves rather than rejects, so this only catches it breaking its contract.
    void start().catch(() => {
      if (!abandoned) {
        showFailure(UNREACHABLE)
      }
    })

    return () => {
      abandoned = true
    }
  }, [
    dealId,
    portalId,
    companyId,
    requestKey,
    navigate,
    displayNotification,
    showFailure
  ])

  if (wrongWorkspace) {
    return (
      <Center height="100%">
        <ErrorMessage
          title="This deal belongs to a different Sequence workspace."
          message="Switch workspace from the menu, then open the link from HubSpot again."
          data-testid="hubspotCreateQuote.wrongWorkspace"
        />
      </Center>
    )
  }

  return (
    <Center height="100%">
      <Spinner />
    </Center>
  )
}
